// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom"; // TODO: useRouter → useNavigate (nav(x) → nav(x), router.replace(x) → nav(x, {replace:true}))
import { useTranslations } from "next-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, FileText, UploadCloud } from "lucide-react";
import { businessTypeOptions } from "@chotca/config";
import {
  Button,
  Card,
  FormField,
  Input,
  KycStatusBadge,
  Select,
  Skeleton,
  useToast,
} from "@chotca/ui";
import { ErrorState } from "@/components/error-state";
import { useMe } from "@chotca/api-client";
import {
  useOrg,
  useOrgKyc,
  useSubmitOrgKyc,
  type SubmitOrgKycInput,
} from "@chotca/api-client";

const ALLOWED = new Set(["image/jpeg", "image/png", "application/pdf"]);
const MAX_BYTES = 20 * 1024 * 1024;

type DocPick = NonNullable<SubmitOrgKycInput["taxDoc"]> | null;

const kycFormSchema = z.object({
  legalName: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập tên pháp lý")
    .max(200, "Tên pháp lý tối đa 200 ký tự"),
  taxCode: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập mã số thuế")
    .max(20, "Mã số thuế tối đa 20 ký tự"),
  repName: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập người đại diện")
    .max(120, "Người đại diện tối đa 120 ký tự"),
  businessType: z
    .string()
    .trim()
    .min(1, "Vui lòng chọn loại hình kinh doanh")
    .max(60, "Loại hình tối đa 60 ký tự"),
  consent: z.literal(true, { message: "Vui lòng đồng ý điều khoản" }),
});

type KycFormValues = z.infer<typeof kycFormSchema>;

export function KycSubmitClient() {
  const t = useTranslations("orgSettings");
  const nav = useNavigate();
  const { toast } = useToast();

  const me = useMe();
  const membership = me.data?.memberships[0];
  const orgId = membership?.orgId;
  const isOwner = membership?.role === "OWNER";

  const org = useOrg(orgId);
  const kyc = useOrgKyc(orgId);
  const submit = useSubmitOrgKyc(orgId ?? "");

  // Files + their progress stay in local state — File objects aren't form
  // values RHF can register; RHF owns the text fields + consent only.
  const [registrationDoc, setRegistrationDoc] = useState<DocPick>(null);
  const [taxDoc, setTaxDoc] = useState<DocPick>(null);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<KycFormValues>({
    resolver: zodResolver(kycFormSchema),
    mode: "onTouched",
    defaultValues: {
      legalName: "",
      taxCode: "",
      repName: "",
      businessType: "",
      consent: false as unknown as true,
    },
  });

  // Pre-fill from existing org / previous submission once data lands.
  useEffect(() => {
    if (hydrated || !org.data) return;
    const prev = kyc.data;
    reset({
      legalName: prev?.legalName ?? org.data.name ?? "",
      taxCode: prev?.taxCode ?? org.data.taxCode ?? "",
      repName: prev?.representativeName ?? org.data.contactName ?? "",
      businessType: prev?.businessType ?? org.data.businessType ?? "",
      consent: false as unknown as true,
    });
    setHydrated(true);
  }, [hydrated, org.data, kyc.data, reset]);

  if (org.isLoading || kyc.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (org.isError || !org.data) {
    return <ErrorState onRetry={() => org.refetch()} />;
  }

  if (!isOwner) {
    return (
      <div className="space-y-6">
        <BackLink t={t} />
        <Card className="p-6">
          <p className="text-body text-ink/70">
            Chỉ chủ doanh nghiệp (OWNER) mới có thể gửi hồ sơ KYC.
          </p>
        </Card>
      </div>
    );
  }

  const status = org.data.kycStatus;
  const isPending = status === "PENDING";
  const isApproved = status === "APPROVED";
  const canSubmit = !isPending && !isApproved;

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    // The registration doc lives outside RHF, so gate it manually.
    if (!registrationDoc) {
      setError(t("kycRequired"));
      return;
    }
    try {
      setUploadPct(0);
      await submit.mutateAsync({
        legalName: values.legalName,
        taxCode: values.taxCode,
        representativeName: values.repName,
        businessType: values.businessType,
        registrationDoc,
        taxDoc: taxDoc ?? undefined,
        onProgress: setUploadPct,
      });
      toast({ tone: "success", title: t("kycSubmitSuccess") });
      nav("/org");
    } catch (e) {
      const msg =
        e instanceof Error && e.message ? e.message : t("kycSubmitError");
      setError(msg);
      toast({ tone: "error", title: msg });
    }
  });

  return (
    <div className="space-y-6">
      <BackLink t={t} />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-display-lg font-extrabold text-ink">
            {t("kycPageTitle")}
          </h1>
          <p className="mt-1 max-w-xl text-body text-ash">
            {t("kycPageSubtitle")}
          </p>
        </div>
        <KycStatusBadge status={status} />
      </header>

      {org.data.kycRejectReason && status === "REJECTED" ? (
        <div className="rounded-md border-l-4 border-danger bg-danger/5 p-4">
          <p className="text-body-sm font-bold uppercase text-danger">
            {t("kycRejectReason")}
          </p>
          <p className="mt-1 text-body text-ink">{org.data.kycRejectReason}</p>
        </div>
      ) : null}

      {isPending ? (
        <div className="rounded-md border-l-4 border-sun bg-sun/10 p-4 text-body text-ink">
          {t("kycPendingNotice")}
        </div>
      ) : null}

      {isApproved ? (
        <div className="rounded-md border-l-4 border-lime bg-lime/15 p-4 text-body text-ink">
          {t("kycApprovedNotice")}
        </div>
      ) : null}

      <Card className="p-6">
        <h2 className="text-title-lg font-bold text-ink">{t("kycTitle")}</h2>

        <form onSubmit={onSubmit} noValidate>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <FormField
              label={t("kycLegalName")}
              error={errors.legalName?.message}
            >
              <Input
                disabled={!canSubmit}
                placeholder="Công ty TNHH ABC"
                invalid={!!errors.legalName}
                {...register("legalName")}
              />
            </FormField>
            <FormField
              label={t("fieldTaxCode")}
              error={errors.taxCode?.message}
            >
              <Input
                disabled={!canSubmit}
                placeholder="0312345678"
                invalid={!!errors.taxCode}
                {...register("taxCode")}
              />
            </FormField>
            <FormField
              label={t("kycRepresentative")}
              error={errors.repName?.message}
            >
              <Input
                disabled={!canSubmit}
                placeholder="Nguyễn Văn A"
                invalid={!!errors.repName}
                {...register("repName")}
              />
            </FormField>
            <FormField
              label={t("fieldBusinessType")}
              error={errors.businessType?.message}
            >
              <Controller
                control={control}
                name="businessType"
                render={({ field }) => (
                  <Select
                    disabled={!canSubmit}
                    invalid={!!errors.businessType}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Chọn loại hình kinh doanh"
                    options={businessTypeOptions(field.value)}
                  />
                )}
              />
            </FormField>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <FilePickField
              label={t("kycRegistrationDoc")}
              hint={t("kycRegistrationDocHint")}
              disabled={!canSubmit}
              value={registrationDoc}
              onChange={setRegistrationDoc}
              onError={(msg) => setError(msg)}
              t={t}
              required
            />
            <FilePickField
              label={t("kycTaxDoc")}
              hint={t("kycTaxDocHint")}
              disabled={!canSubmit}
              value={taxDoc}
              onChange={setTaxDoc}
              onError={(msg) => setError(msg)}
              t={t}
            />
          </div>

          <label className="mt-6 flex cursor-pointer items-start gap-3 text-body-sm text-ink/80">
            <input
              type="checkbox"
              disabled={!canSubmit}
              className="mt-1 h-4 w-4 accent-lime"
              {...register("consent")}
            />
            <span>{t("kycConsent")}</span>
          </label>
          {errors.consent ? (
            <p className="mt-1 text-body-sm text-danger">
              {errors.consent.message}
            </p>
          ) : null}

          {error ? (
            <p className="mt-4 rounded-md bg-danger/10 px-3 py-2 text-body-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex justify-end">
            <Button
              type="submit"
              disabled={!canSubmit || submit.isPending}
              loading={submit.isPending}
            >
              {submit.isPending
                ? `${t("kycSubmitting")} ${uploadPct}%`
                : t("kycSubmitCta")}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function BackLink({ t }: { t: (k: string) => string }) {
  return (
    <Link
      to="/org"
      className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-ink/70 hover:text-ink"
    >
      <ArrowLeft className="h-4 w-4" />
      {t("kycBack")}
    </Link>
  );
}

function FilePickField({
  label,
  hint,
  disabled,
  value,
  required,
  onChange,
  onError,
  t,
}: {
  label: string;
  hint: string;
  disabled?: boolean;
  required?: boolean;
  value: DocPick;
  onChange: (v: DocPick) => void;
  onError: (msg: string) => void;
  t: (k: string) => string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handlePick(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED.has(file.type)) {
      onError(t("kycFileInvalidType"));
      return;
    }
    if (file.size > MAX_BYTES) {
      onError(t("kycFileTooLarge"));
      return;
    }
    onChange({
      file,
      contentType: file.type as "image/jpeg" | "image/png" | "application/pdf",
    });
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-body-sm font-semibold text-ink">
        <span>{label}</span>
        {required ? <span className="text-danger">*</span> : null}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className={[
          "group flex w-full flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-6 text-center transition",
          disabled
            ? "cursor-not-allowed border-smoke bg-mist/40 opacity-60"
            : value
              ? "border-lime bg-lime/5 hover:bg-lime/10"
              : "border-smoke bg-paper hover:border-ink/40 hover:bg-mist",
        ].join(" ")}
      >
        {value ? (
          <>
            <FileText className="h-7 w-7 text-ink/70" />
            <p className="mt-2 max-w-full truncate text-body-sm font-semibold text-ink">
              {value.file.name}
            </p>
            <p className="text-xs text-ash">
              {(value.file.size / 1024 / 1024).toFixed(2)} MB
            </p>
            <span className="mt-2 text-xs font-semibold text-ink/70 underline">
              {t("kycChangeFile")}
            </span>
          </>
        ) : (
          <>
            <UploadCloud className="h-7 w-7 text-ink/50" />
            <p className="mt-2 text-body-sm font-semibold text-ink">
              {t("kycChooseFile")}
            </p>
            <p className="mt-0.5 text-xs text-ash">{hint}</p>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,application/pdf"
        disabled={disabled}
        onChange={(e) => {
          handlePick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </div>
  );
}
